import { useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Bot, Maximize2, Minimize2, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { AgentTraceView } from "./AgentTraceView";

export function AgentTracePanel({
  seedRunId,
  onClose,
}: {
  seedRunId: string | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [isFullscreen, setIsFullscreen] = useState(false);
  const content = (
    <>
      <div className="fixed inset-0 z-[100] bg-foreground/10" onClick={onClose} aria-hidden />
      <div
        className={cn(
          "fixed top-0 z-[101] flex h-full flex-col bg-card shadow-lg animate-fade-in transition-all",
          isFullscreen ? "inset-0 border-0" : "right-0 w-[var(--detail-panel-width)] border-l border-border/50"
        )}
      >
        <div className="flex shrink-0 items-center justify-between border-b border-border/50 px-5 py-4">
          <div className="flex min-w-0 items-center gap-2">
            <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10">
              <Bot className="h-4 w-4 text-primary" strokeWidth={2} />
            </span>
            <h2 className="truncate text-[16px] font-semibold leading-5 text-foreground">{t("studioData.tabs.trace.panelTitle")}</h2>
          </div>
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => setIsFullscreen((value) => !value)}
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-muted-foreground hover:bg-muted/60 transition-colors"
              aria-label={
                isFullscreen
                  ? t("studioData.tabs.trace.exitFullscreenAria")
                  : t("studioData.tabs.trace.enterFullscreenAria")
              }
            >
              {isFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-muted-foreground hover:bg-muted/60 transition-colors"
              aria-label={t("studioData.tabs.trace.closeAria")}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 scrollbar-thin">
          <AgentTraceView layout="embedded" seedRunId={seedRunId} />
        </div>
      </div>
    </>
  );

  if (typeof document === "undefined") return null;
  return createPortal(content, document.body);
}
